/**
 * Pure helpers behind TranslatedAnswer (#2109 R2-B2, B5, B14). Kept apart from
 * the component so the component file exports only a component (react-refresh).
 */
import { sourceHashSync } from '@/lib/formI18n';
import type { AnswerTranslation, AnswerTranslations } from '@/types/applications';

/**
 * The English to show for `original`, or null when the original must be shown.
 * Exported so non-visual readers (sorting, copy-to-clipboard) apply the same rule.
 */
export function usableTranslation(
  original: string | null | undefined,
  translation: AnswerTranslation | null | undefined,
): string | null {
  if (!translation || typeof original !== 'string' || !original.trim()) return null;
  if (translation.same_as_original) return null;
  const text = typeof translation.text === 'string' ? translation.text.trim() : '';
  if (!text) return null;
  if (typeof translation.source_hash !== 'string' || !/^[0-9a-f]{64}$/.test(translation.source_hash)) return null;
  if (translation.source_hash !== sourceHashSync(original)) return null;
  if (text === original.trim()) return null;
  return translation.text;
}

/**
 * The English translation of one answer, from `answer_translations`.
 *
 * `field_id` can be dotted (`prior_hardship.notes`). Stored through a dotted
 * `$set` it nests, so both the flat key and the nested walk are read.
 */
export function answerTranslationFor(
  translations: AnswerTranslations | null | undefined,
  section: string,
  fieldId: string,
): AnswerTranslation | undefined {
  const bySection = translations?.[section] as Record<string, unknown> | undefined;
  if (!bySection || typeof bySection !== 'object') return undefined;
  const flat = bySection[fieldId] as Record<string, unknown> | undefined;
  let node: unknown = flat;
  if (!node) {
    node = fieldId.split('.').reduce<unknown>(
      (acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined),
      bySection,
    );
  }
  const en = node && typeof node === 'object' ? (node as Record<string, unknown>).en : undefined;
  return en && typeof en === 'object' ? (en as AnswerTranslation) : undefined;
}

/**
 * R2-B3: names are never translated. A field whose id ends in `name`
 * (`first_name`, `mother_full_name`, `referral_contact_name`) renders with
 * `translate="no"`, so neither a stored translation nor the browser's own
 * page translation replaces it.
 */
export function isNameField(fieldId: string): boolean {
  return /(^|[._])name$/.test(fieldId);
}

/**
 * Victor 2026-09-30 R1: which written answers staff read in English. Mirror of
 * `backend/src/lib/answerTranslatable.js` — read that file's header for the
 * why. A `text`/`textarea` answer is translated unless the question asks for
 * an identifier (a name, a contact detail, a place, a date, a code), decided
 * from `format` and the field id; `translate_answer: true` forces it on. Both
 * sides are pinned against the same seeded questions.
 */
const TRANSLATABLE_ANSWER_TYPES: readonly string[] = ['text', 'textarea'];
const STRONG_IDENTIFIER_PARTS = new Set([
  'email', 'phone', 'mobile', 'tel', 'zip', 'zipcode', 'postal', 'postcode', 'ssn', 'url', 'website', 'dob',
]);
const TRAILING_IDENTIFIER_PARTS = new Set([
  'name', 'names', 'street', 'apt', 'unit', 'city', 'state', 'province', 'country', 'address',
  'location', 'hospital', 'hospitals', 'clinic', 'worker', 'date', 'birth', 'birthday', 'birthdate',
  'code', 'id', 'number',
]);

export interface AnswerFieldShape {
  /** The field id (`FieldSpec.key` on the intake renderers). */
  id: string;
  type: string;
  format?: string | null;
  translate_answer?: boolean;
}

export function isIdentifierField(field: Pick<AnswerFieldShape, 'id' | 'format'>): boolean {
  if (typeof field.format === 'string' && field.format) return true;
  const parts = String(field.id ?? '').toLowerCase().split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return false;
  if (parts.some((p) => STRONG_IDENTIFIER_PARTS.has(p))) return true;
  return TRAILING_IDENTIFIER_PARTS.has(parts[parts.length - 1]);
}

export function isTranslatableAnswerField(field: AnswerFieldShape): boolean {
  if (!TRANSLATABLE_ANSWER_TYPES.includes(field.type)) return false;
  if (field.translate_answer === true) return true;
  return !isIdentifierField(field);
}

/**
 * The model judged the answer needs no translation (R2-B5: a number, a place
 * name) — a well-formed entry, made from this exact answer, marked so. Not
 * "untranslated": nothing is missing.
 */
export function isSameAsOriginal(
  original: string | null | undefined,
  translation: AnswerTranslation | null | undefined,
): boolean {
  if (!translation || typeof original !== 'string' || !original.trim()) return false;
  if (translation.source_hash !== sourceHashSync(original)) return false;
  return translation.same_as_original === true
    || (typeof translation.text === 'string' && translation.text.trim() === original.trim());
}

/**
 * Mirror of `backend/src/lib/answerLanguage.js` `looksNonEnglish` (Victor
 * 2026-11-02) — read that file's header for the why. An application's
 * `language` is the screen the family used, not the language they wrote in, so
 * an answer on an English-stamped record is checked here: one that looks
 * non-English and has no English is "Not translated yet", with a "Translate to
 * English" action. Both sides are pinned against the same cases.
 */
const ENGLISH_WORDS = new Set([
  'the', 'and', 'of', 'to', 'in', 'is', 'was', 'our', 'my', 'we', 'with', 'for', 'that', 'his', 'her',
  'has', 'have', 'been', 'when', 'because', 'also', 'from', 'very', 'but', 'this', 'they', 'she', 'he',
  'it', 'are', 'were', 'had', 'not', 'at', 'on', 'an', 'be', 'i', 'you', 'us', 'so', 'all', 'their',
  'there', 'which', 'will', 'would', 'can', 'after', 'since', 'every', 'each',
]);
const OTHER_WORDS = new Set([
  'de', 'la', 'el', 'los', 'las', 'que', 'y', 'con', 'para', 'por', 'una', 'es', 'su', 'sus', 'mi', 'mis',
  'nuestra', 'nuestro', 'nuestros', 'muy', 'pero', 'está', 'fue', 'años', 'cuando', 'tiene', 'porque',
  'también', 'desde', 'hemos', 'sido', 'más', 'del', 'se', 'ya', 'hay', 'esta', 'este', 'eso',
  'não', 'uma', 'com', 'da', 'dos', 'das', 'nós', 'meu', 'minha', 'é', 'são', 'foi', 'muito', 'mas',
  'le', 'les', 'des', 'et', 'est', 'une', 'du', 'pour', 'pas', 'nous', 'mon', 'mes', 'avec', 'dans',
  'je', 'il', 'elle', 'mais', 'très', 'qui', 'au',
]);
const ACCENT_MARKS = /[áéíóúñãõçàèìòùâêîôûœ¿¡]/giu;

export function looksNonEnglish(text: string | null | undefined): boolean {
  if (typeof text !== 'string') return false;
  const letters = text.match(/\p{L}/gu) ?? [];
  if (letters.length < 2) return false;
  const latin = letters.filter((c) => /\p{Script=Latin}/u.test(c)).length;
  if (latin / letters.length < 0.5) return true;
  const words = text.toLowerCase().match(/\p{L}+/gu) ?? [];
  let en = 0;
  let other = 0;
  for (const w of words) {
    if (ENGLISH_WORDS.has(w)) en += 1;
    if (OTHER_WORDS.has(w)) other += 1;
  }
  // Each accent mark counts as evidence too (mirror of the backend rule).
  const marks = (text.match(ACCENT_MARKS) ?? []).length;
  return other + marks > en;
}
